import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, MapPin, CalendarDays, BadgeDollarSign } from "lucide-react";
import { IMAGES, TUITION, upcomingIntakes } from "./hcaData";
import { HcaLeadForm } from "./HcaLeadForm";

const fade = (d) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 0.6 } });

const Badges = () => (
    <motion.ul {...fade(0.3)} className="mt-8 grid grid-cols-2 gap-3 max-w-lg text-sm">
        {[
            [ShieldCheck, "PTIB-approved diploma"],
            [MapPin, "Surrey, BC campus"],
            [CalendarDays, "Job-ready in 25 weeks"],
            [BadgeDollarSign, `Tuition ${TUITION}`],
        ].map(([Icon, t]) => (
            <li key={t} className="flex items-center gap-2 text-white/90">
                <Icon className="h-4 w-4 text-[#D4A140] shrink-0" /> {t}
            </li>
        ))}
    </motion.ul>
);

export const HcaHero = () => {
    const next = upcomingIntakes()[0];
    return (
        <section id="hca-hero" data-testid="hca-hero-section" className="relative overflow-hidden bg-[#0E1E3A]">
            <img src={IMAGES.hero} alt="Health care assistant supporting an elderly client" className="absolute inset-0 h-full w-full object-cover object-[center_30%] opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0E1E3A] via-[#0E1E3A]/80 to-[#0E1E3A]/20" />
            <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7">
                    {next && (
                        <motion.span {...fade(0)} data-testid="hca-hero-intake-pill" className="inline-flex items-center gap-2 rounded-full border border-[#D4A140]/50 bg-[#D4A140]/10 px-3 py-1 text-xs font-medium text-[#F1D9A6]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#D4A140] animate-pulse" /> Next intake {next} — seats filling
                        </motion.span>
                    )}
                    <motion.h1 {...fade(0.1)} className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mt-5 tracking-tight">
                        Become a certified <span className="italic text-[#D4A140]">Health Care Assistant</span> in BC.
                    </motion.h1>
                    <motion.p {...fade(0.2)} className="mt-6 text-base lg:text-lg text-white/75 max-w-xl leading-relaxed">
                        Train for one of Canada's most in-demand front-line careers. 25 weeks of hands-on learning,
                        210 hours of real clinical placement, and a provincially recognized diploma.
                    </motion.p>
                    <Badges />
                </div>
                <motion.div {...fade(0.25)} id="hca-form" className="lg:col-span-5 scroll-mt-24">
                    <div className="rounded-2xl bg-[#F9F8F6] p-6 sm:p-8 shadow-2xl ring-1 ring-white/10" data-testid="hca-lead-card">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4A140] font-semibold">Free info session</p>
                        <h2 className="font-display text-2xl text-[#0E1E3A] mt-1 mb-5">Talk to an admissions advisor</h2>
                        <HcaLeadForm />
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
